import TypesenseInstantSearchAdapter from 'typesense-instantsearch-adapter';
import processProxy from '@/env/processProxy';
import type {MortgageRealEstateOffer, MortgageRealEstateSortProperty} from '@/modules/calculator-v25/types/mortgage/realEstates';

const PROPERTY_COLLECTION = 'property';

// Typesense returns the whole document unless the query lists the fields it should send back.
const INCLUDED_FIELDS: (keyof MortgageRealEstateOffer)[] = [
  'id', 'images', 'thumbnail', 'priceAmount', 'priceCurrency', 'pricePerSquareMeterAmount',
  'pricePerSquareMeterCurrency', 'rooms', 'areaValue', 'areaUnit', 'floor', 'totalFloors',
  'locationCity', 'locationDistrict', 'marketType', 'createdAt', 'updatedAt', 'status',
];

type MortgageRealEstatesSearchParams = {
  filterBy: string;
  sortBy: MortgageRealEstateSortProperty;
  page: number;
  perPage: number;
};

type MortgageRealEstatesSearchResult = {
  offers: MortgageRealEstateOffer[];
  found: number;
};

class MortgageRealEstatesApi {
  private searchClient: any = null;

  // `typesense-instantsearch-adapter` is the only Typesense package in package.json and its
  // `typesenseClient` is the plain search-only client — the same one the real-estates listing
  // hands down to its filter cards. Built lazily so a missing env var breaks the tab, not the chunk.
  private get client(): any {
    if (!this.searchClient) {
      this.searchClient = new TypesenseInstantSearchAdapter({
        server: {
          apiKey: processProxy.VUE_APP_TYPESENSE_API_KEY,
          nodes: [{host: processProxy.VUE_APP_TYPESENSE_HOST, port: 443, protocol: 'https',},],
        },
        additionalSearchParameters: {queryBy: '*',},
      }).typesenseClient;
    }
    return this.searchClient;
  }

  public async searchOffers({filterBy, sortBy, page, perPage,}: MortgageRealEstatesSearchParams): Promise<MortgageRealEstatesSearchResult> {
    const response = await this.client.collections(PROPERTY_COLLECTION).documents().search({
      q: '*',
      query_by: '*',
      filter_by: filterBy,
      // Dates are day-granular, so ties are common — breaking them the same way round lets a reversed sort reverse them too.
      sort_by: `${sortBy},_seq_id:${sortBy.split(':')[1]}`,
      include_fields: INCLUDED_FIELDS.join(','),
      page,
      per_page: perPage,
    });
    return {
      offers: (response.hits ?? []).map((hit: {document: MortgageRealEstateOffer;}) => hit.document),
      found: response.found ?? 0,
    };
  }
}

export default new MortgageRealEstatesApi();
